import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { Clock } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import {
  ArticleForm,
  type ArticleFormData,
  type ArticleFormHandle,
} from "~/components/cih/article-form";
import { Button, ButtonRouter } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { ConfirmDialog } from "~/components/ui/confirm-dialog";
import { PageTitle } from "~/components/ui/page-title";
import { RichTextViewer } from "~/components/ui/rich-text-editor";
import { Skeleton } from "~/components/ui/skeleton";
import {
  archiveMyArticleFn,
  articleQueryKeys,
  createMyArticleDraftFn,
  myArticleQueryOptions,
  myArticlesQueryOptions,
  updateArticleFn,
  submitArticleForReviewFn,
  withdrawArticleFromReviewFn,
  type ArticleRow,
} from "~/lib/functions/cih/articles";
import { publishedChallengesQueryOptions } from "~/lib/functions/cih/challenges";
import type { ContentStatus } from "~/lib/shared/content-status";
import type { JsonValue } from "~/lib/shared/json";

import { ArticleStatusBadge } from "../-components/article-status-badge";

export const Route = createFileRoute("/_user/my-coaching/articles/$articleId")({
  staticData: { title: "Edit article" },
  loader: async ({ context, params }) => {
    const detailOptions = myArticleQueryOptions(params.articleId);
    // Seed the detail cache from the list cache so hover preloads on the
    // articles table don't each fire a redundant getMyArticleFn round-trip
    // (the list query already returns full ArticleRow data).
    if (
      context.queryClient.getQueryData(detailOptions.queryKey) === undefined
    ) {
      const list = context.queryClient.getQueryData<ArticleRow[]>(
        articleQueryKeys.mine,
      );
      const fromList = list?.find((a) => a.id === params.articleId);
      if (fromList) {
        context.queryClient.setQueryData(detailOptions.queryKey, fromList);
      }
    }
    const data = await context.queryClient.ensureQueryData(detailOptions);
    if (!data) throw notFound();
    return { title: data.title };
  },
  notFoundComponent: ArticleNotFound,
  component: EditArticlePage,
});

function ArticleNotFound() {
  return (
    <div className="mx-auto max-w-3xl">
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
          <PageTitle>Article not found</PageTitle>
          <p className="text-text-secondary max-w-md">
            This article doesn't exist, was archived, or isn't one of yours.
          </p>
          <ButtonRouter to="/my-coaching/articles" variant="primary">
            Back to your articles
          </ButtonRouter>
        </CardContent>
      </Card>
    </div>
  );
}

function bodyToString(body: JsonValue | null): string {
  if (!body) return "";
  if (typeof body === "string") return body;
  return JSON.stringify(body);
}

function EditArticlePage() {
  const { articleId } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmWithdrawOpen, setConfirmWithdrawOpen] = useState(false);
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const formRef = useRef<ArticleFormHandle>(null);
  const { data: article, isLoading } = useQuery(
    myArticleQueryOptions(articleId),
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: myArticleQueryOptions(articleId).queryKey,
    });
    void queryClient.invalidateQueries({
      queryKey: myArticlesQueryOptions().queryKey,
    });
  };

  const updateMutation = useMutation({
    mutationFn: (data: ArticleFormData) =>
      updateArticleFn({
        data: {
          id: articleId,
          title: data.title,
          description: data.description,
          body: data.body,
        },
      }),
    onSuccess: () => {
      // Clear the dirty baseline so leaving the page after a save doesn't
      // trigger the unsaved-changes guard.
      formRef.current?.markSaved();
      toast.success("Article saved");
      invalidate();
    },
    onError: (err) => {
      console.error("Failed to save article:", err);
      toast.error(
        err instanceof Error ? err.message : "Failed to save article",
      );
    },
  });

  const submitMutation = useMutation({
    mutationFn: async (data: ArticleFormData) => {
      await updateArticleFn({
        data: {
          id: articleId,
          title: data.title,
          description: data.description,
          body: data.body,
        },
      });
      await submitArticleForReviewFn({ data: { id: articleId } });
    },
    onSuccess: () => {
      formRef.current?.markSaved();
      toast.success("Article submitted for review");
      invalidate();
    },
    onError: (err) => {
      console.error("Failed to submit article:", err);
      toast.error(
        err instanceof Error ? err.message : "Failed to submit article",
      );
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: () => withdrawArticleFromReviewFn({ data: { id: articleId } }),
    onSuccess: () => {
      toast.success("Submission withdrawn — back to draft");
      setConfirmWithdrawOpen(false);
      invalidate();
    },
    onError: (err) => {
      console.error("Failed to withdraw article:", err);
      toast.error("Failed to withdraw submission");
    },
  });

  const editPublishedMutation = useMutation({
    mutationFn: () =>
      createMyArticleDraftFn({ data: { publishedId: articleId } }),
    onSuccess: (draft) => {
      void queryClient.invalidateQueries({
        queryKey: myArticlesQueryOptions().queryKey,
      });
      navigate({
        to: "/my-coaching/articles/$articleId",
        params: { articleId: draft.id },
      });
    },
    onError: (err) => {
      console.error("Failed to open editor for published article:", err);
      toast.error("Could not open article for editing");
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveMyArticleFn({ data: { id: articleId } }),
    onSuccess: (result) => {
      toast.success(result.discarded ? "Edits discarded" : "Draft archived");
      setConfirmArchiveOpen(false);
      void queryClient.invalidateQueries({
        queryKey: myArticlesQueryOptions().queryKey,
      });
      navigate({ to: "/my-coaching/articles" });
    },
    onError: (err) => {
      console.error("Failed to archive article:", err);
      toast.error("Failed to archive article");
    },
  });

  if (isLoading || !article) {
    return (
      <div className="max-w-4xl space-y-8">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[400px] w-full rounded-2xl" />
      </div>
    );
  }

  const status = article.status as ContentStatus;
  const isDraft = status === "draft";
  const isInReview = status === "in_review";
  const isPublished = status === "published";
  const isEditOfPublished = article.draftOf != null;
  const isPending = updateMutation.isPending || submitMutation.isPending;

  return (
    <div className="max-w-4xl space-y-8">
      {/* Sticky header */}
      <div className="sticky top-(--app-topbar-h) z-40 flex h-16 items-center gap-4 bg-white/50 backdrop-blur-lg">
        <PageTitle className="grow">{article.title}</PageTitle>
        <ArticleStatusBadge status={status} />
        {isPublished && (
          <Button
            variant="primary"
            size="sm"
            loading={editPublishedMutation.isPending}
            disabled={editPublishedMutation.isPending}
            onClick={() => editPublishedMutation.mutate()}
          >
            Edit
          </Button>
        )}
        {isInReview && (
          <Button
            variant="default"
            size="sm"
            onClick={() => setConfirmWithdrawOpen(true)}
            disabled={withdrawMutation.isPending}
          >
            Withdraw to edit
          </Button>
        )}
        {isDraft && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmArchiveOpen(true)}
            disabled={archiveMutation.isPending}
          >
            {isEditOfPublished ? "Discard edits" : "Archive"}
          </Button>
        )}
      </div>

      {isEditOfPublished && isDraft && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          You're editing a live article. Save your changes, then submit for
          editorial review — once approved, your edits replace the live version.
        </div>
      )}

      {isInReview && (
        <div className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div className="space-y-1">
            <p className="font-medium">
              Your article is in the editorial review queue
            </p>
            <p className="text-amber-800">
              {isEditOfPublished
                ? "We'll publish your edits to the live article once a reviewer approves them. No further action needed — you'll be notified when it goes live."
                : "We'll publish it on the Coaching Intelligence Hub once a reviewer approves it. No further action needed — you'll be notified when it goes live."}{" "}
              If you spotted something you want to change, withdraw it back to
              draft.
            </p>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmWithdrawOpen}
        onOpenChange={setConfirmWithdrawOpen}
        title="Withdraw submission?"
        description="Your article will move back to draft so you can keep editing. Resubmit when you're ready and it'll re-enter the editorial queue."
        confirmLabel="Withdraw to draft"
        loadingLabel="Withdrawing..."
        variant="default"
        onConfirm={() => withdrawMutation.mutate()}
        isLoading={withdrawMutation.isPending}
      />

      <ConfirmDialog
        open={confirmArchiveOpen}
        onOpenChange={setConfirmArchiveOpen}
        title={isEditOfPublished ? "Discard edits?" : "Archive draft?"}
        description={
          isEditOfPublished
            ? "Your unsaved edits to this live article will be permanently deleted. The published article stays exactly as it is."
            : "This draft will be moved to your archived articles and removed from your active list."
        }
        confirmLabel={isEditOfPublished ? "Discard edits" : "Archive draft"}
        loadingLabel={isEditOfPublished ? "Discarding..." : "Archiving..."}
        variant="destructive"
        onConfirm={() => archiveMutation.mutate()}
        isLoading={archiveMutation.isPending}
      />

      {isDraft ? (
        <ArticleForm
          ref={formRef}
          defaultValues={{
            title: article.title,
            description: article.description,
            challengeId: article.challengeId,
            body: bodyToString(article.body),
          }}
          reviewNote={article.reviewNote}
          isPending={isPending}
          onSave={(data) => updateMutation.mutate(data)}
          onSubmitForReview={(data) => submitMutation.mutate(data)}
          saveLabel="Save"
          submitLabel={
            isEditOfPublished ? "Submit Edits for Review" : "Submit for Review"
          }
        />
      ) : (
        <ReadOnlyView article={article} />
      )}
    </div>
  );
}

function ReadOnlyView({ article }: { article: ArticleRow }) {
  const { data: challenges = [] } = useQuery(publishedChallengesQueryOptions);
  const challengeName =
    challenges.find((c) => c.id === article.challengeId)?.title ??
    "Unknown challenge";

  return (
    <Card>
      <dl className="space-y-4">
        <div className="space-y-1">
          <dt className="eyebrow text-gray-500">Challenge</dt>
          <dd className="text-text-secondary text-sm">{challengeName}</dd>
        </div>
        <div className="space-y-1">
          <dt className="eyebrow text-gray-500">Description</dt>
          <dd className="text-text-secondary text-sm">{article.description}</dd>
        </div>
        <div className="space-y-1">
          <dt className="eyebrow text-gray-500">Body</dt>
          <dd>
            <RichTextViewer content={bodyToString(article.body)} />
          </dd>
        </div>
      </dl>
    </Card>
  );
}
